import { config } from "./vars";
import { getCurrentSession } from "../lib/supabase";

function get_random_text(len: number) {
  const lorem =
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum";
  const lorem_len = lorem.length;

  const iterations = Math.floor(len / lorem_len);
  const additional_chars = len - lorem_len * iterations;

  return lorem.repeat(iterations) + lorem.slice(0, additional_chars);
}

interface Starter {
  label: string;
}

interface Expert {
  value: string;
  label: string;
  summary: string;
  friendlyName: string;
  starters: Array<Starter>;
  prompt: string;
  path: string;
  idleVideoUrl: string;
  image?: string;
  subtitle?: string;
  heygen_avatar_id?: string;
  isPublished?: boolean;
  showOnHomepage?: boolean;
  sortOrder?: number;
  /** YouTube video ID for intro video above expert description (hidden once chat or video call starts) */
  introVideoId?: string;
  /** Custom thumbnail URL for the intro video */
  introVideoThumbnail?: string;
}

type APIStarter = {
  label: string;
  icon?: string;
};

type APIExpert = {
  id: number;
  base_prompt?: string;
  speaking_style?: string;
  background?: string;
  avoid?: string;
  rules?: string;
  only_discuss?: string;
  chat_name: string;
  friendly_name: string;
  avatar_image_url?: string;
  voice_id?: string;
  idle_video_url?: string;
  heygen_avatar_id?: string;
  bio?: string;
  subtitle?: string;
  intro_video_id?: string;
  intro_video_thumbnail_url?: string;
  conversation_starters?: APIStarter[];
  is_published?: boolean;
  show_on_homepage?: boolean;
  sort_order?: number;
};

type APIExpertInsert = {
  base_prompt?: string;
  speaking_style?: string;
  background?: string;
  avoid?: string;
  rules?: string;
  only_discuss?: string;
  friendly_name: string;
};

/** Full display name from chat_name slug or human-readable chat_name. */
function expertDisplayName(e: APIExpert): string {
  if (e.chat_name.includes(" ")) {
    return e.chat_name;
  }
  if (e.friendly_name.includes(" ")) {
    return e.friendly_name;
  }
  return e.chat_name
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function parseStarters(raw: APIStarter[] | undefined): Starter[] {
  if (!raw?.length) {
    return [];
  }
  return raw
    .filter((s) => s?.label)
    .map((s) => ({ label: s.label }));
}

export function apiExpertToExpert(e: APIExpert): Expert {
  return {
    value: e.id.toString(),
    label: expertDisplayName(e),
    friendlyName: e.friendly_name,
    path: "/qa/" + e.id.toString(),
    summary: e.bio || "",
    subtitle: e.subtitle || "",
    prompt: e.base_prompt || "",
    starters: parseStarters(e.conversation_starters),
    idleVideoUrl: e.idle_video_url || "",
    heygen_avatar_id: e.heygen_avatar_id || "",
    image: e.avatar_image_url || undefined,
    introVideoId: e.intro_video_id || undefined,
    introVideoThumbnail: e.intro_video_thumbnail_url || undefined,
    isPublished: e.is_published ?? false,
    showOnHomepage: e.show_on_homepage ?? false,
    sortOrder: e.sort_order ?? e.id,
  };
}

export function expertsToMap(experts: APIExpert[]): Map<string, Expert> {
  const map = new Map<string, Expert>();
  for (const e of experts) {
    map.set(e.id.toString(), apiExpertToExpert(e));
  }
  return map;
}

export function filterHomepageExperts(experts: APIExpert[]): APIExpert[] {
  return experts.filter((e) => e.show_on_homepage === true);
}

export function homepageExpertsToMap(experts: APIExpert[]): Map<string, Expert> {
  return expertsToMap(filterHomepageExperts(experts));
}

interface User {
  id: number;
  username: string;
  name: string;
  email: string;
  sub: string;
}

interface Answer {
  answer: string;
  stream_chunks: any[];
  audio?: string[]; // Base64 encoded PCM audio chunks from Fish Audio
}

interface LiveAvatarToken {
  session_id: string | null;
  session_token: string | null;
}

async function submitQuestionWithHist(
  expert_id: string,
  chatHistory: Array<string>,
  customPrompt = "",
  video = false,
  chatSessionId: string | null = null,
  useHeyGen = false,
  heygenSessionId: string | null = null,
  heygenAccessToken: string | null = null
): Promise<Answer> {
  const data: Record<string, unknown> = {
    prompt: "",
    chat_history: chatHistory,
  };

  if (useHeyGen && heygenSessionId && heygenAccessToken) {
    data.session_id = heygenSessionId;
    data.access_token = heygenAccessToken;
  }

  const params = chatSessionId
    ? "?" + new URLSearchParams({ sid: chatSessionId }).toString()
    : "";
  const url =
    (useHeyGen
      ? config.api_base + "/heygen/" + expert_id + "/answer"
      : video
        ? config.api_base + "/video/" + expert_id + "/stream/qa"
        : config.api_base + "/qa/" + expert_id) + params;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-cors-api-key": "temp_169be7a128bcb8e4669a4c363d866f8b",
      Authorization: `Bearer ${(await getCurrentSession())?.session?.access_token || ""}`,
    },
    body: JSON.stringify(data),
  });

  const resp = await response.json();
  return resp;
}

async function getHeyGenAccessToken(): Promise<string | null> {
  try {
    const response = await fetch(config.api_base + "/heygen/access-token", {
      method: "GET",
      headers: {
        "x-cors-api-key": "temp_169be7a128bcb8e4669a4c363d866f8b",
      },
    });
    const resp = await response.json();
    return resp.token || null;
  } catch (error) {
    console.error("Error fetching HeyGen access token:", error);
    return null;
  }
}

async function getLiveAvatarToken(): Promise<LiveAvatarToken> {
  try {
    const response = await fetch(config.api_base + "/heygen/access-token", {
      method: "GET",
      headers: {
        "x-cors-api-key": "temp_169be7a128bcb8e4669a4c363d866f8b",
      },
    });
    const resp = await response.json();
    return {
      session_id: resp.session_id || null,
      session_token: resp.session_token || null,
    };
  } catch (error) {
    console.error("Error fetching LiveAvatar token:", error);
    return { session_id: null, session_token: null };
  }
}

async function getExperts(): Promise<Array<APIExpert>> {
  const response = await fetch(config.api_base + "/qa/experts", {
    method: "GET",
    headers: {
      "x-cors-api-key": "temp_169be7a128bcb8e4669a4c363d866f8b",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to load experts (${response.status})`);
  }

  return response.json();
}

async function fetchExpertsMap(): Promise<Map<string, Expert>> {
  const apiExperts = await getExperts();
  return expertsToMap(apiExperts);
}

async function fetchHomepageExpertsMap(): Promise<Map<string, Expert>> {
  const apiExperts = await getExperts();
  return homepageExpertsToMap(apiExperts);
}

export {
  type Expert,
  type Starter,
  type User,
  type APIExpert,
  type APIExpertInsert,
  type LiveAvatarToken,
  get_random_text,
  getExperts,
  fetchExpertsMap,
  fetchHomepageExpertsMap,
  expertDisplayName,
  submitQuestionWithHist,
  getHeyGenAccessToken,
  getLiveAvatarToken,
};
